import * as T from 'common/types';
import { LogEntryEdge } from '../../../common/graphqlTypes';

/**
 * Get span associated with given spanId. If the span doesn't exists in know spans, we add it with default values.
 * @param spans   - key value pair of spanId to Span of known spans
 * @param spanId  - spanId we're searching for.
 */
function getSpan(spans: Record<string, T.Span>, spanId: string): T.Span {
  let span = spans[spanId];

  if (!span) {
    spans[spanId] = {
      spanId,
      startTime: 0,
      endTime: 0,
      level: 0,
    };
    span = spans[spanId];
  }
  return span;
}

/**
 * Get tags from the log line if there are any and add them to span.tags.
 * If tags contain component or service, add those as properties on the span.
 *
 * @param {Object} span The span to add tags to
 * @param {Object} line The line to get tags from to add to span
 */
function addTags(
  span: T.Span,
  // TODO: Fix the type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  line: Record<string, any>,
): void {
  if (line.tags) {
    span.tags = line.tags;

    if (line.tags.component) {
      span.component = line.tags.component;
    }
    if (line.tags.service) {
      span.service = line.tags.service;
    }
  }
}

/**
 * Add `children` and `next` properties to spans that have children or younger siblings.
 * We get this information from a child or younger sibling span which will have
 *
 * references: { child_of: parentSpan } or
 * references: { follows_from: siblingSpan }
 *
 * We take that child_of ID and use it to look up the parent span, and add a `children` array to the
 * parent and push all children into it.  Same idea for follows_from, we look up the follows_from
 * span and add a property to it called `next` pointing at the current span (its younger sibling).
 *
 * There's also a possibility that a span could have both `child_of` AND `follows_from`, but
 * for now we'll only use follows_from if there is no child_of property since there's no
 * clear answer for what to do if a span has both. Note: This comment follows from legacy code.
 *
 * @param  span   - The span that will be a root, child_of, or follows_from another span
 * @param  line   - The line that has info about a span's relationship status
 * @param  root   - Root span
 * @param  spans  - Record of all the spans
 */
function addRelationshipPointers(
  span: T.Span,
  // TODO: Fix the type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  line: Record<string, any>,
  root: T.Span,
  spans: Record<string, T.Span>,
): void {
  const refs = line.references;
  if (refs) {
    if (refs.child_of) {
      const parent = getSpan(spans, refs.child_of);
      parent.children = parent.children || [];
      if (!parent.children.some((child) => child.spanId === line.spanId)) {
        parent.children.push(getSpan(spans, line.spanId));
      }
    } else if (refs.follows_from) {
      const elderSib = getSpan(spans, refs.follows_from);
      elderSib.next = line.spanId;
    }
  } else {
    root.spanId = line.spanId;
  }
}

/**
 * Recursively loop through spans starting with the root span and place all related
 * spans into traceData array in the order in which they will be drawn in the UI.
 * Children are always drawn before siblings. E.g:
 *
 * span1
 *    span2
 *        span3
 *        span4
 *    span5
 *    span6
 *        span7
 *            span8
 *        span9
 *    span10
 *
 * @param span      - Current span being processed
 * @param level     - The level (how many ancestors deep) for this span
 * @param traceData - Array containing gathered trace data.
 * @param spans     - Record of all the spans
 */
function generateSpansArray(
  span: T.Span,
  level: number,
  traceData: Array<T.Span>,
  spans: Record<string, T.Span>,
): Array<T.Span> {
  if (!span) {
    return traceData;
  }

  span.level = level;
  traceData.push(span);

  if (span.children) {
    span.children.sort((a, b) => spans[a.spanId].startTime - spans[b.spanId].startTime);
    span.children.forEach((child) => generateSpansArray(spans[child.spanId], level + 1, traceData, spans));
  }

  if (span.next) {
    return generateSpansArray(spans[span.next.spanId], level, traceData, spans);
  }
  return traceData;
}

/**
 * Given an array of log lines for a particular trace, produce an array of spans
 * that represent the trace. For every span there will be at least 2 logs: an event
 * will be logged when the span starts, and another when the span ends.  A span may
 * also log its own events while that span is running. For every log within a span
 * there will be a corresponding log event.
 *
 * Example:
 *
 * {"ts":1541795130671,"elapsed":0,"spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","action":"start","operation":"timeline","tags":{"request":"x205403154","user":"u8704782"},"baggage":{"traceId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","reporter.spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149"}}
 * {"ts":1541795130679,"elapsed":8,"spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","action":"log","operation":"timeline","fields":{"field1":"I am a field","field2":"I am also a field"},"tags":{"request":"x205403154","user":"u8704782","status":"success"},"baggage":{"traceId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","reporter.spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149"}}
 * {"ts":1541795130683,"elapsed":12,"spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","action":"log","operation":"timeline","fields":{"field3":"I too am a field","field4":"I too am a field as well"},"tags":{"request":"x205403154","user":"u8704782","status":"success"},"baggage":{"traceId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","reporter.spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149"}}
 * {"ts":1541795130686,"elapsed":15,"spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","action":"finish","operation":"timeline","tags":{"request":"x205403154","user":"u8704782","status":"success"},"baggage":{"traceId":"723a4ea1-eb6b-4399-a0bc-46325df9d149","reporter.spanId":"723a4ea1-eb6b-4399-a0bc-46325df9d149"}}
 *
 * After processing, this span should contain:
 * {
 *    startTime: 1541795130671,
 *    endTime: 1541795130686,
 *    operation: "timeline",
 *    component: string,  // optional, doesn't exist in this example
 *    service: string,    // optional, doesn't exist in this example
 *    tags: {             // optional, will be empty if no tags
 *      request: "x205403154",
 *      user: "u8704782",
 *      status: "success"
 *    },
 *    logs: [             // optional, will be empty if no logs
 *      {
 *        timestamp: 1541795130679,
 *        fields: {
 *          field1: "I am a field",
 *          field2: "I am also a field"
 *        }
 *      },
 *      {
 *        timestamp: 1541795130683,
 *        fields: {
 *          field3: "I too am a field",
 *          field4: "I too am a field as well"
 *        }
 *      }
 *    ],
 *
 * }
 *
 * Note:
 * Tags are cumulative, so we only grab them from the "finish" log event.
 * Baggage is the same on every event for the same span.
 * We use `references` to order the spans in this.traceData.
 *
 * @param {Array} results An array of log events for this trace
 */
export function convertResultsToTraceData(results: Array<LogEntryEdge>): T.Span[] {
  let traceEndTime = 0;
  const spans: Record<string, T.Span> = {};
  const root = {} as T.Span;

  for (let i = 0, len = results.length; i < len; i++) {
    const result = results[i].logEntry;
    const line = JSON.parse(result.values.message);
    const span = getSpan(spans, line.spanId);

    if (line.action === 'start') {
      span.startTime = line.ts;
      span.operation = line.operation;
    } else if (line.action === 'finish') {
      span.endTime = line.ts;
      addTags(span, line);

      // Find the end time of the trace - unclear yet if the root span always contains
      // all other spans entire time ranges, though I suspect so.
      if (line.ts > traceEndTime) {
        traceEndTime = span.endTime;
      }
      span.serverInfo = result.serverInfo;
    } else if (line.action === 'log') {
      span.logs = span.logs || [];
      span.logs.push({ timestamp: line.ts, fields: line.fields });
    }

    addRelationshipPointers(span, line, root, spans);
  }
  const traceData: Array<T.Span> = [];
  return generateSpansArray(spans[root.spanId], 0, traceData, spans);
}
